/**
 * CropRecommendationForm.tsx
 * 
 * Form component for generating crop-specific nutrient recommendations
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import Grid from '../../components/common/Grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import {
  fetchAvailableCropTypes,
  generateCropRecommendation,
  selectCropRecommendationData,
  selectCropRecommendationStatus,
  selectAvailableCropTypes
} from '../../store/slices/cropRecommendationSlice';
import { WaterQualityParameters } from '../../types/waterQuality';

interface CropRecommendationFormProps {
  waterQuality?: WaterQualityParameters;
  onRecommendationGenerated?: (recommendation: any) => void;
}

const CropRecommendationForm: React.FC<CropRecommendationFormProps> = ({
  waterQuality,
  onRecommendationGenerated
}) => {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectCropRecommendationStatus);
  const recommendations = useAppSelector(selectCropRecommendationData);
  const { cropTypes, specificCrops, growthStages } = useAppSelector(selectAvailableCropTypes);
  
  const [formState, setFormState] = useState({
    cropType: '',
    specificCrop: '',
    growthStage: '',
    includeWaterQuality: Boolean(waterQuality),
    includeEnvironment: false,
    temperature: 'normal',
    light: 'normal',
    humidity: 'normal'
  });
  
  // Available crops and stages for selected crop type
  const [availableCrops, setAvailableCrops] = useState<string[]>([]);
  const [availableStages, setAvailableStages] = useState<string[]>([]);
  
  // Load available crop types when component mounts
  useEffect(() => {
    dispatch(fetchAvailableCropTypes());
  }, [dispatch]);
  
  // Update available specific crops when crop type changes
  useEffect(() => {
    if (formState.cropType && specificCrops[formState.cropType]) {
      setAvailableCrops(specificCrops[formState.cropType]);
      setAvailableStages(growthStages[formState.cropType] || []);
      
      // Reset specific crop and growth stage if not available for new crop type
      if (formState.specificCrop && !specificCrops[formState.cropType].includes(formState.specificCrop)) {
        setFormState(prev => ({ ...prev, specificCrop: '' }));
      }
      
      if (formState.growthStage && (!growthStages[formState.cropType] || 
          !growthStages[formState.cropType].includes(formState.growthStage))) {
        setFormState(prev => ({ ...prev, growthStage: '' }));
      }
    } else {
      setAvailableCrops([]);
      setAvailableStages([]);
    }
  }, [formState.cropType, specificCrops, growthStages]);
  
  // Notify parent component when recommendations are generated
  useEffect(() => {
    if (recommendations && onRecommendationGenerated) {
      onRecommendationGenerated(recommendations);
    }
  }, [recommendations, onRecommendationGenerated]);
  
  const handleCropTypeChange = (event: SelectChangeEvent) => {
    setFormState({
      ...formState,
      cropType: event.target.value,
      specificCrop: '',
      growthStage: ''
    });
  };
  
  const handleSpecificCropChange = (event: SelectChangeEvent) => {
    setFormState({
      ...formState,
      specificCrop: event.target.value
    });
  };
  
  const handleGrowthStageChange = (event: SelectChangeEvent) => {
    setFormState({
      ...formState,
      growthStage: event.target.value
    });
  };
  
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({
      ...formState,
      [event.target.name]: event.target.checked
    });
  };
  
  const handleEnvironmentChange = (event: SelectChangeEvent) => {
    setFormState({
      ...formState,
      [event.target.name]: event.target.value
    });
  };
  
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    
    const environmentalConditions = formState.includeEnvironment ? {
      temperature: formState.temperature === 'low' ? 'cool' as const :
                  formState.temperature === 'normal' ? 'moderate' as const : 'warm' as const,
      lightIntensity: formState.light === 'normal' ? 'medium' as const :
                     formState.light === 'low' ? 'low' as const : 'high' as const,
      humidity: formState.humidity === 'normal' ? 'medium' as const :
               formState.humidity === 'low' ? 'low' as const : 'high' as const
    } : undefined;
    
    dispatch(generateCropRecommendation({
      cropType: formState.cropType,
      specificCrop: formState.specificCrop || '',
      growthStage: formState.growthStage || '',
      waterQuality: formState.includeWaterQuality ? waterQuality : undefined,
      environmentalFactors: environmentalConditions
    }));
  };
  
  const isLoading = status === 'loading';
  const isError = status === 'failed';
  
  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Crop Selection
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={3}>
            <Grid xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel id="crop-type-label">Crop Type</InputLabel>
                <Select
                  labelId="crop-type-label"
                  id="crop-type"
                  value={formState.cropType}
                  onChange={handleCropTypeChange}
                  label="Crop Type"
                  disabled={isLoading || cropTypes.length === 0}
                >                  {cropTypes.map((type: string) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="specific-crop-label">Specific Crop</InputLabel>
                <Select
                  labelId="specific-crop-label"
                  id="specific-crop"
                  value={formState.specificCrop}
                  onChange={handleSpecificCropChange}
                  label="Specific Crop"
                  disabled={isLoading || !formState.cropType || availableCrops.length === 0}
                >
                  <MenuItem value="">
                    <em>Any</em>
                  </MenuItem>                  {availableCrops.map((crop: string) => (
                    <MenuItem key={crop} value={crop}>{crop}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="growth-stage-label">Growth Stage</InputLabel>
                <Select
                  labelId="growth-stage-label"
                  id="growth-stage"
                  value={formState.growthStage}
                  onChange={handleGrowthStageChange}
                  label="Growth Stage"
                  disabled={isLoading || !formState.cropType || availableStages.length === 0}
                >
                  <MenuItem value="">
                    <em>Default (Vegetative)</em>
                  </MenuItem>                  {availableStages.map((stage: string) => (
                    <MenuItem key={stage} value={stage}>{stage.charAt(0).toUpperCase() + stage.slice(1)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Additional Recommendation Factors</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formState.includeWaterQuality}
                      onChange={handleCheckboxChange}
                      name="includeWaterQuality"
                      disabled={!waterQuality}
                    />
                  }
                  label="Include water quality in recommendations"
                />
                {!waterQuality && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Water quality data not available. Add water quality parameters for tailored recommendations.
                  </Alert>
                )}
              </FormGroup>
            </Grid>
            
            <Grid xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formState.includeEnvironment}
                      onChange={handleCheckboxChange}
                      name="includeEnvironment"
                    />
                  }
                  label="Include environmental conditions"
                />
              </FormGroup>
            </Grid>
            
            {formState.includeEnvironment && (
              <>
                <Grid xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="temperature-label">Temperature</InputLabel>
                    <Select
                      labelId="temperature-label"
                      id="temperature"
                      name="temperature"
                      value={formState.temperature}
                      onChange={handleEnvironmentChange}
                      label="Temperature"
                    >
                      <MenuItem value="low">Low (&lt;15°C)</MenuItem>
                      <MenuItem value="normal">Normal (15-28°C)</MenuItem>
                      <MenuItem value="high">High (&gt;28°C)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="light-label">Light Level</InputLabel>
                    <Select
                      labelId="light-label"
                      id="light"
                      name="light"
                      value={formState.light}
                      onChange={handleEnvironmentChange}
                      label="Light Level"
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="humidity-label">Humidity</InputLabel>
                    <Select
                      labelId="humidity-label"
                      id="humidity"
                      name="humidity"
                      value={formState.humidity}
                      onChange={handleEnvironmentChange}
                      label="Humidity"
                    >
                      <MenuItem value="low">Low (&lt;40%)</MenuItem>
                      <MenuItem value="normal">Normal (40-70%)</MenuItem>
                      <MenuItem value="high">High (&gt;70%)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={isLoading || !formState.cropType}
          startIcon={isLoading && <CircularProgress size={20} color="inherit" />}
        >
          {isLoading ? 'Generating...' : 'Generate Recommendations'}
        </Button>
      </Box>
      
      {isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to generate recommendations. Please try again.
        </Alert>
      )}
    </Box>
  );
};

export default CropRecommendationForm;
